import "./styles.css";
import {
  PixelDocument,
  createEditorState,
  setColor,
  setTool,
  markSaved,
  beginStroke,
  commitStroke,
  applyDocumentAction,
  undo,
  redo,
  toHexColor,
  serializePixelwerkstattJson,
  type EditorState,
  type Snapshot,
  type ToolId,
  type Rgba,
} from "./core/index.js";

import { Header } from "./ui/header.js";
import { Toolbar } from "./ui/toolbar.js";
import { Sidebar } from "./ui/sidebar.js";
import { CanvasView } from "./ui/canvas.js";
import { StatusBar } from "./ui/statusbar.js";
import { ImportModal } from "./ui/import-modal.js";
import { NewCanvasDialog } from "./ui/dialogs.js";

function isTyping(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

function initApp(): void {
  const appContainer = document.getElementById("app");
  if (!appContainer) {
    throw new Error("Element #app konnte im DOM nicht gefunden werden.");
  }
  appContainer.innerHTML = "";

  // 1. Initial State
  const initialDoc = PixelDocument.create(16, 16);
  let state: EditorState = createEditorState(initialDoc);
  let strokeBefore: Snapshot | null = null;

  // 2. Build Layout Containers
  const headerContainer = document.createElement("header");
  headerContainer.id = "header-container";

  const workspaceContainer = document.createElement("div");
  workspaceContainer.id = "workspace-container";
  workspaceContainer.className = "workspace-layout";

  const toolbarContainer = document.createElement("aside");
  toolbarContainer.id = "toolbar-container";

  const viewportContainer = document.createElement("main");
  viewportContainer.id = "viewport-container";

  const sidebarContainer = document.createElement("aside");
  sidebarContainer.id = "sidebar-container";

  workspaceContainer.appendChild(toolbarContainer);
  workspaceContainer.appendChild(viewportContainer);
  workspaceContainer.appendChild(sidebarContainer);

  const statusbarContainer = document.createElement("footer");
  statusbarContainer.id = "statusbar-container";

  appContainer.appendChild(headerContainer);
  appContainer.appendChild(workspaceContainer);
  appContainer.appendChild(statusbarContainer);

  // 3. Components
  let header!: Header;
  let toolbar!: Toolbar;
  let sidebar!: Sidebar;
  let canvasView!: CanvasView;
  let statusBar!: StatusBar;
  let importModal!: ImportModal;

  const handleUndo = (): void => {
    canvasView.finishInteraction();
    const next = undo(state);
    if (!next) return;
    state = next;
    canvasView.setDocument(state.document);
    updateUI();
    statusBar.setMessage("Aktion rückgängig gemacht");
  };

  const handleRedo = (): void => {
    canvasView.finishInteraction();
    const next = redo(state);
    if (!next) return;
    state = next;
    canvasView.setDocument(state.document);
    updateUI();
    statusBar.setMessage("Aktion wiederholt");
  };

  const updateUI = (): void => {
    header.updateHistory(state.undo.length, state.redo.length);
    header.setDirty(state.dirty);
    header.setDocumentDimensions(state.document.width, state.document.height);
    sidebar.setDimensions(state.document.width, state.document.height);
    statusBar.setSize(state.document.width, state.document.height);
  };

  const selectTool = (toolId: ToolId): void => {
    state = setTool(state, toolId);
    toolbar.setActiveTool(state.tool);
    canvasView.setTool(state.tool);
    statusBar.setTool(state.tool);
  };

  // Header Actions
  header = new Header(headerContainer, {
    onUndo: () => {
      handleUndo();
    },
    onRedo: () => {
      handleRedo();
    },
    onNew: () => {
      canvasView.finishInteraction();
      new NewCanvasDialog({
        initialWidth: state.document.width,
        initialHeight: state.document.height,
        isDirty: state.dirty,
        onCreate: (w, h) => {
          const newDoc = PixelDocument.create(w, h);
          state = createEditorState(newDoc, state.color, state.tool);
          canvasView.setDocument(state.document);
          sidebar.setDimensions(w, h);
          updateUI();
          statusBar.setMessage(`Neue Leinwand angelegt (${w} × ${h} Pixel)`);
        },
      }).open();
    },
    onClear: () => {
      canvasView.finishInteraction();
      if (state.dirty && !window.confirm("Möchtest du wirklich alle Pixel leeren?")) {
        return;
      }
      state = applyDocumentAction(state, "Leinwand geleert", (scratch) => {
        scratch.clearAll();
        return scratch;
      });
      canvasView.render();
      updateUI();
      statusBar.setMessage("Leinwand geleert");
    },
    onImport: () => {
      canvasView.finishInteraction();
      importModal.open();
    },
    onExportPng: () => {
      canvasView.finishInteraction();
      const exportedDoc = state.document.clone();
      const doc = exportedDoc;
      const offscreen = document.createElement("canvas");
      offscreen.width = doc.width;
      offscreen.height = doc.height;
      const ctx = offscreen.getContext("2d");
      if (!ctx) {
        statusBar.setMessage("Fehler: PNG konnte nicht gerendert werden.");
        return;
      }
      const imgData = ctx.createImageData(doc.width, doc.height);
      imgData.data.set(doc.data);
      ctx.putImageData(imgData, 0, 0);

      offscreen.toBlob((blob) => {
        if (!blob) {
          statusBar.setMessage("Fehler: PNG-Blob konnte nicht erzeugt werden.");
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "pixelwerkstatt.png";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 2000);

        if (state.document.equals(exportedDoc)) {
          state = markSaved(state);
          updateUI();
        }
        statusBar.setMessage("PNG-Download gestartet (pixelwerkstatt.png)");
      }, "image/png");
    },
    onExportProject: () => {
      canvasView.finishInteraction();
      const json = serializePixelwerkstattJson(state.document);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pixelwerkstatt.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);

      state = markSaved(state);
      updateUI();
      statusBar.setMessage("Projekt-Download gestartet (pixelwerkstatt.json)");
    },
  });

  // Toolbar Actions
  toolbar = new Toolbar(toolbarContainer, (toolId) => {
    selectTool(toolId);
  });

  // Sidebar Actions
  sidebar = new Sidebar(sidebarContainer, {
    onColorChange: (color: Rgba) => {
      state = setColor(state, color);
      canvasView.setColor(state.color);
      sidebar.setColor(state.color);
    },
    onResizeCanvas: (w: number, h: number) => {
      canvasView.finishInteraction();
      if (w === state.document.width && h === state.document.height) return;
      state = applyDocumentAction(state, "Größe geändert", (scratch) => {
        return scratch.resize(w, h);
      });
      canvasView.setDocument(state.document);
      updateUI();
      statusBar.setMessage(`Größe geändert auf ${w} × ${h} Pixel`);
    },
  });

  // Canvas Viewport Actions
  canvasView = new CanvasView(viewportContainer, state.document, state.color, {
    onStrokeBegin: () => {
      strokeBefore = beginStroke(state);
    },
    onStrokeCommit: (toolLabel: string) => {
      if (strokeBefore) {
        state = commitStroke(state, strokeBefore, toolLabel);
        strokeBefore = null;
        updateUI();
      }
    },
    onHoverCell: (cell) => {
      statusBar.setCoords(cell);
    },
    onSampleColor: (color) => {
      if (color.a === 0) {
        statusBar.setMessage("Pixel ist transparent (keine Farbe aufgenommen)");
      } else {
        state = setColor(state, color);
        sidebar.setColor(state.color);
        canvasView.setColor(state.color);
        statusBar.setMessage(`Farbe übernommen: ${toHexColor(color)}`);
      }
    },
    onZoomChange: (zoom) => {
      statusBar.setMessage(`Zoom: ${zoom * 100}%`);
    },
  });

  // Status Bar
  statusBar = new StatusBar(statusbarContainer);

  // Import Modal
  importModal = new ImportModal({
    isDocumentDirty: () => state.dirty,
    onImportSuccess: (importedDoc) => {
      canvasView.finishInteraction();
      state = applyDocumentAction(state, "Importiert", () => importedDoc);
      canvasView.setDocument(state.document);
      sidebar.setDimensions(state.document.width, state.document.height);
      updateUI();
      statusBar.setMessage(
        `Importiert (${state.document.width} × ${state.document.height} Pixel)`,
      );
    },
  });

  // Synchronize initial state across components
  toolbar.setActiveTool(state.tool);
  sidebar.setColor(state.color);
  sidebar.setDimensions(state.document.width, state.document.height);
  statusBar.setTool(state.tool);
  statusBar.setSize(state.document.width, state.document.height);
  updateUI();

  // Keyboard Shortcuts
  window.addEventListener("keydown", (e) => {
    if (isTyping(e.target)) return;

    // Undo: Ctrl+Z or Cmd+Z (without Shift)
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") {
      e.preventDefault();
      handleUndo();
      return;
    }

    // Redo: Ctrl+Shift+Z / Cmd+Shift+Z or Ctrl+Y / Cmd+Y
    if (
      ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "z") ||
      ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y")
    ) {
      e.preventDefault();
      handleRedo();
      return;
    }

    // Tool Shortcuts: P, E, I (no modifier keys)
    if (!e.ctrlKey && !e.metaKey && !e.altKey) {
      if (e.key.toLowerCase() === "p") {
        e.preventDefault();
        selectTool("pencil");
        return;
      }
      if (e.key.toLowerCase() === "e") {
        e.preventDefault();
        selectTool("eraser");
        return;
      }
      if (e.key.toLowerCase() === "i") {
        e.preventDefault();
        selectTool("eyedropper");
        return;
      }
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        canvasView.zoomIn();
        return;
      }
      if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        canvasView.zoomOut();
        return;
      }
      if (e.key === "0") {
        e.preventDefault();
        canvasView.fitToViewport();
        return;
      }
    }
  });
}

// Run app once DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initApp);
} else {
  initApp();
}
